import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { ArrowLeft, Printer } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmationModal';
import { deleteOrder, getOrderById } from '../../hooks/orders';
import { getCompanyName, Order } from '../../utils/types';
import {
  OrderField,
  OrderFulfillmentBadge,
  OrderHero,
  OrderPaymentBadge,
  OrderPrimaryButton,
  OrderSectionCard,
  OrderShell,
  OrderStatusBadge,
  formatOrderDate,
  formatOrderMoney,
} from './components/orderUi';

const getOrderCustomer = (order: Order) =>
  `${order.lead?.title ? `${order.lead.title} ` : ''}${order.lead?.first_name || ''} ${order.lead?.second_name || ''}`.trim() ||
  'Guest customer';

export default function ViewOrderPage() {
  const { orgId, orderId } = useParams<{ orgId: string; orderId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      const response = await getOrderById(orgId, orderId);
      if (mounted && response.success && response.data && typeof response.data === 'object') {
        setOrder(response.data as Order);
      }
      if (mounted) {
        setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [orderId, orgId]);

  const handleDelete = async () => {
    if (!orgId || !order?.id) {
      return;
    }

    setDeleting(true);
    const response = await deleteOrder(orgId, order.id);
    setDeleting(false);

    if (!response.success) {
      toast({
        title: 'Could not delete order',
        description: 'Please try again in a moment.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Order deleted',
      description: `${order.reference || 'Order'} was removed.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    navigate(`/organization/${orgId}/orders`);
  };

  if (loading || !order) {
    return (
      <OrderShell compact>
        <OrderSectionCard title="Loading order" description="Please wait while the order is loading.">
          <Button variant="ghost" isLoading loadingText="Loading" />
        </OrderSectionCard>
      </OrderShell>
    );
  }

  return (
    <OrderShell compact>
      <OrderHero
        eyebrow="Order"
        title={order.reference || 'Order details'}
        description="Review the customer, items, delivery progress, and next steps from one clear order record."
        primaryAction={
          <OrderPrimaryButton
            leftIcon={<EditIcon />}
            onClick={() => navigate(`/organization/${orgId}/orders/${order.id}/edit`)}
          >
            Edit order
          </OrderPrimaryButton>
        }
        secondaryAction={
          <HStack spacing={3}>
            <Button borderRadius="16px" variant="outline" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button borderRadius="16px" variant="outline" leftIcon={<Printer size={16} />} onClick={() => window.print()}>
              Print
            </Button>
          </HStack>
        }
        stats={[
          { label: 'Total', value: formatOrderMoney(order.total_amount) },
          { label: 'Status', value: order.status?.toUpperCase() || 'DRAFT' },
          { label: 'Payment', value: order.payment_status?.replace(/_/g, ' ') || 'UNPAID' },
          { label: 'Created', value: formatOrderDate(order.created_at) },
        ]}
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <OrderSectionCard
            title="Customer context"
            description="The customer and company details linked to this order."
            action={<OrderStatusBadge status={order.status} />}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <OrderField label="Customer" value={getOrderCustomer(order)} />
              <OrderField label="Company" value={getCompanyName(order.lead?.company) || 'No company'} />
              <OrderField label="Email" value={order.lead?.email || 'No email'} />
              <OrderField label="Phone" value={order.lead?.phone || 'No phone'} />
            </SimpleGrid>
          </OrderSectionCard>

          <OrderSectionCard
            title="Line items"
            description="Everything included in this order."
          >
            <TableContainer>
              <Table variant="simple" size="md">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Item</Th>
                    <Th>SKU</Th>
                    <Th isNumeric>Quantity</Th>
                    <Th isNumeric>Unit price</Th>
                    <Th isNumeric>Total</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(order.items || []).map((item) => (
                    <Tr key={String(item.id)}>
                      <Td>
                        <VStack align="flex-start" spacing={1}>
                          <Text fontWeight="700">{item.product_name}</Text>
                          {item.description ? (
                            <Text fontSize="sm" color="gray.500">
                              {item.description}
                            </Text>
                          ) : null}
                        </VStack>
                      </Td>
                      <Td>{item.sku || 'N/A'}</Td>
                      <Td isNumeric>{item.quantity}</Td>
                      <Td isNumeric>{formatOrderMoney(item.unit_price)}</Td>
                      <Td isNumeric fontWeight="700">
                        {formatOrderMoney(item.total_price)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </OrderSectionCard>

          <OrderSectionCard
            title="Notes"
            description="Customer-facing and internal notes captured on this order."
          >
            <Stack spacing={5}>
              <Box>
                <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
                  Customer notes
                </Text>
                <Text color="gray.700" whiteSpace="pre-wrap">
                  {order.notes || 'No customer-facing notes.'}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
                  Internal notes
                </Text>
                <Text color="gray.700" whiteSpace="pre-wrap">
                  {order.internal_notes || 'No internal notes.'}
                </Text>
              </Box>
            </Stack>
          </OrderSectionCard>
        </Stack>

        <Stack spacing={6}>
          <OrderSectionCard
            title="Operational summary"
            description="Pricing, delivery, and order status in one place."
          >
            <Stack spacing={4}>
              <OrderField label="Reference" value={order.reference || 'Reference pending'} />
              <OrderField label="Status" value={<OrderStatusBadge status={order.status} />} />
              <OrderField label="Payment" value={<OrderPaymentBadge status={order.payment_status} />} />
              <OrderField
                label="Delivery"
                value={<OrderFulfillmentBadge status={order.fulfillment_status} />}
              />
              <OrderField label="Source" value={order.source || 'Not set'} />
              <OrderField label="Priority" value={order.priority || 'Normal'} />
              <OrderField label="Delivery method" value={order.delivery_method || 'Not set'} />
              <OrderField label="Delivery date" value={formatOrderDate(order.requested_delivery_date)} />
              <OrderField label="Delivery address" value={order.delivery_address || 'No address set'} />
              <Box pt={2}>
                <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
                  Total
                </Text>
                <Text fontSize="3xl" fontWeight="800" color="gray.900">
                  {formatOrderMoney(order.total_amount)}
                </Text>
              </Box>
            </Stack>
          </OrderSectionCard>

          <OrderSectionCard
            title="Actions"
            description="Make changes or remove the order when needed."
          >
            <VStack align="stretch" spacing={3}>
              <OrderPrimaryButton
                leftIcon={<EditIcon />}
                onClick={() => navigate(`/organization/${orgId}/orders/${order.id}/edit`)}
              >
                Edit order
              </OrderPrimaryButton>
              <Button
                borderRadius="16px"
                variant="ghost"
                colorScheme="red"
                leftIcon={<DeleteIcon />}
                onClick={onOpen}
              >
                Delete order
              </Button>
            </VStack>
          </OrderSectionCard>
        </Stack>
      </SimpleGrid>

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        handleConfirm={handleDelete}
        isLoading={deleting}
        AreYouSureQuestion={`Delete ${order.reference || 'this order'}?`}
        AreYouSureTextExplanation="This order and all of its items will be permanently removed."
        confirmButtonText="Delete order"
        confirmButtonColor="#E53E3E"
        colorScheme="red"
      />
    </OrderShell>
  );
}
